/**
 * Where to send someone after a login completes.
 *
 * The target travels as a `redirect` query param on the login and handoff
 * routes, so a deep link survives the trip through authentication — whether
 * the person signed in with a password or arrived from the old portal with a
 * login code.
 *
 * The param is attacker-influenced (it's just a URL), so only in-app paths are
 * honoured: anything absolute, protocol-relative or pointing back into /auth
 * is discarded rather than followed.
 */

export const sanitizeRedirect = value => {
  if (typeof value !== 'string' || value === '' || value === '/' || value.length > 2000) {
    return null
  }
  // absolute URLs ("https://...", "javascript:...") and protocol-relative
  // ("//evil.com") targets would leave the app — only plain in-app paths pass
  if (!value.startsWith('/') || value.startsWith('//') || value.includes(':')) {
    return null
  }
  // browsers treat backslashes as slashes when parsing URLs ("/\evil.com"
  // becomes "//evil.com"), and control characters have no business in a path
  // eslint-disable-next-line no-control-regex
  if (value.includes('\\') || /[\x00-\x1f\x7f]/.test(value)) {
    return null
  }
  // landing back inside the auth flow would loop
  if (/^\/auth(\/|$)/i.test(value)) {
    return null
  }
  return value
}

/**
 * Resolves the post-login destination from a route's query string, falling
 * back to the home route.
 */
export const loginRedirectTarget = search => {
  const redirect = new URLSearchParams(search || '').get('redirect')
  return sanitizeRedirect(redirect) || '/'
}
